import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  addAlertNote,
  bulkAssignAlerts,
  bulkCloseAlerts,
  bulkEscalateAlerts,
} from '../api'
import type {
  AddNotePayload,
  BulkAssignPayload,
  BulkClosePayload,
  BulkEscalatePayload,
} from '../types'

/**
 * AF-126 — alert mutations.
 *
 * Bulk endpoints are used per the technical notes ("Bulk assign uses the
 * backend bulk endpoint, not N individual calls"). Optimistic updates
 * happen at the React Query cache level so the UI feels instant; we roll
 * back on error.
 */

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['alerts'] })
}

export function useBulkAssign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkAssignPayload) => bulkAssignAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Assigned ${data.updated} alert${data.updated === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    onError: () => toast.error("Couldn't assign alerts"),
  })
}

export function useBulkClose() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkClosePayload) => bulkCloseAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Closed ${data.updated} alert${data.updated === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    onError: () => toast.error("Couldn't close alerts"),
  })
}

export function useBulkEscalate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: BulkEscalatePayload) => bulkEscalateAlerts(payload),
    onSuccess: (data) => {
      toast.success(`Escalated ${data.updated} alert${data.updated === 1 ? '' : 's'}`)
      invalidateAll(qc)
    },
    onError: () => toast.error("Couldn't escalate alerts"),
  })
}

export function useAddNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AddNotePayload) => addAlertNote(payload),
    onSuccess: (_data, variables) => {
      toast.success('Note added')
      void qc.invalidateQueries({ queryKey: ['alerts', 'detail', variables.alert_id] })
    },
    onError: () => toast.error("Couldn't add note"),
  })
}
